// @ts-strict-ignore
/**
 * @fileoverview utilities around developer permissions
 */

import { exhaustiveCheck } from "@lib/exhaustiveCheck";
import {
  getPermissionScope,
  getPermission,
  getPermissionScopeValue,
  getPermissionValue,
} from "@lib/permissionConstants";
import type { Permission } from "@lib/permissionConstants";
import type { PermissionScope } from "@lib/permissionConstants";

import type {
  DeveloperPermissionPayload,
  DeveloperPermission,
  PermissionContext,
  UnassignedPermission,
} from "./types";

/** turns a permission object into a DeveloperPermission */
export const permissionsMapper = (p: DeveloperPermissionPayload): DeveloperPermission | UnassignedPermission => {
  const scope = getPermissionScope(p.scope);
  const name = getPermission(p.name);

  if (!scope || !name) {
    return {
      name: "",
      scope: "",
    } as UnassignedPermission;
  }

  switch (scope) {
    case "company":
      return {
        scope,
        name,
        companyId: p.scope_id,
      };
    case "appGroup":
      return {
        scope,
        name,
        appGroupId: p.scope_id,
      };
    case "team":
      return {
        scope,
        name,
        teamId: p.scope_id,
      };
    default:
      exhaustiveCheck(scope);
  }
};

export const mapToPermission = (
  permission: Permission,
  scope: PermissionScope,
  {
    companyId,
    appGroupId,
    teamId,
  }: {
    companyId: string;
    appGroupId: string;
    teamId?: string;
  }
): DeveloperPermissionPayload => {
  return {
    scope: getPermissionScopeValue(scope),
    name: getPermissionValue(permission),
    scope_id: scope === "company" ? companyId : scope === "appGroup" ? appGroupId : teamId,
    context: {
      ...(scope === "company"
        ? {}
        : scope === "appGroup"
          ? { company_id: companyId }
          : // scope === "team"
            { company_id: companyId, app_group_id: appGroupId }),
    },
  };
};

/**
 * Function to determine if the given collection of permissions and context should grant
 * the given permission. Permissions work by progressively widening scopes in conjuction
 * with specific permissions at a given scope (or "admin" permission at that scope).
 *
 * @param collection the set of permissions belonging to a `Developer`
 * @param context the scope context in which the permission should be checked
 * @param permission the `Permission` being checked
 *
 * @example
 * ```tsx
 * const developer: Developer = {...}
 * const canSendPermissions: boolean = hasPermission(
 *   developer.permission
 *   { companyId: "foo", appGroupId: "bar", teamId: "baz" },
 *   "sendCampaigns"
 * );
 * ```
 * @note the collection effectively is a property of a Developer
 */
export const hasPermission = (
  collection: DeveloperPermission[],
  context: PermissionContext,
  permission: Permission
): boolean => {
  const hasCompanyLevelPermission = collection.some((p) => {
    // "admin" permissions are a blanket permission
    // otherwise, the permission can be explicitly scoped to a company
    return (
      p.scope === "company" && p.companyId === context.companyId && (p.name === "admin" || p.name === permission)
    );
  });

  if (hasCompanyLevelPermission) {
    // early return because we don't have to do further checks
    return true;
  }

  if (context.appGroupId) {
    // "admin" permissions are a blanket permission
    // otherwise, the permission can be explicitly scoped to an app group
    const hasAppGroupLevelPermission = collection.some((p) => {
      return (
        p.scope === "appGroup" &&
        p.appGroupId === context.appGroupId &&
        (p.name === "admin" || p.name === permission)
      );
    });

    if (hasAppGroupLevelPermission) {
      // early return because we don't have to check teams
      return true;
    }
  }

  if (context.teamId) {
    const hasTeamLevelPermission = collection.some((p) => {
      return p.scope === "team" && p.teamId === context.teamId && (p.name === "admin" || p.name === permission);
    });

    if (hasTeamLevelPermission) {
      return true;
    }
  }

  return false;
};
